import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Upload } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/helpconnect-resources`;
const EMPTY = { title: '', description: '', category: '', resource_type: 'link', url: '', states: [], featured: false };

const StatePicker = ({ all, value, onChange }) => {
  const toggle = (s) => onChange(value.includes(s) ? value.filter((x) => x !== s) : [...value, s]);
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={value.length === 0} onCheckedChange={() => onChange([])} data-testid="resource-states-all" />
        All states (applies everywhere)
      </label>
      <div className="grid grid-cols-5 sm:grid-cols-9 gap-1 max-h-36 overflow-y-auto border rounded-md p-2">
        {all.map((s) => (
          <label key={s} className="flex items-center gap-1 text-xs">
            <Checkbox checked={value.includes(s)} onCheckedChange={() => toggle(s)} data-testid={`resource-state-${s}`} />{s}
          </label>
        ))}
      </div>
    </div>
  );
};

export const HcResourceFormDialog = ({ open, onOpenChange, resource, categories, states, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setForm(resource ? { ...EMPTY, ...resource, url: resource.url || '' } : EMPTY); setFile(null); }
  }, [open, resource]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async (publish) => {
    if (!form.title.trim() || !form.category) return toast.error('Add a title and choose a category');
    if (form.resource_type === 'document' && !file && !resource?.file_name) return toast.error('Choose a document to upload');
    setSaving(true);
    try {
      const body = { ...form, url: form.resource_type === 'link' ? form.url : null };
      let r = resource ? (await axios.put(`${API}/${resource.id}`, body)).data : (await axios.post(API, body)).data;
      if (form.resource_type === 'document' && file) {
        const fd = new FormData();
        fd.append('file', file);
        r = (await axios.post(`${API}/${r.id}/document`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
      }
      if (publish) r = (await axios.post(`${API}/${r.id}/publish`)).data;
      toast.success(publish ? 'Resource published' : 'Resource saved');
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the resource');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="resource-form-dialog">
        <DialogHeader>
          <DialogTitle>{resource ? 'Edit Resource' : 'Add Resource'}</DialogTitle>
          <DialogDescription>Published resources appear in the HELPConnect Resources Center right away.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => set('title', e.target.value)} maxLength={150} data-testid="resource-title-input" /></div>
          <div><Label>Short description</Label><Textarea value={form.description} onChange={(e) => set('description', e.target.value)} maxLength={600} rows={3} data-testid="resource-description-input" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => set('category', v)}>
                <SelectTrigger data-testid="resource-category-select"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`resource-category-${c.id}`}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.resource_type} onValueChange={(v) => set('resource_type', v)}>
                <SelectTrigger data-testid="resource-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="link" data-testid="resource-type-link">Link</SelectItem>
                  <SelectItem value="document" data-testid="resource-type-document">Document</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.resource_type === 'link' ? (
            <div><Label>Link</Label><Input value={form.url} onChange={(e) => set('url', e.target.value)} placeholder="https://" data-testid="resource-url-input" /></div>
          ) : (
            <div>
              <Label>Document</Label>
              <label className="mt-1 flex items-center gap-2 border border-dashed rounded-md p-3 cursor-pointer text-sm hover:bg-muted/50">
                <Upload className="h-4 w-4" />
                <span className="truncate">{file ? file.name : resource?.file_name ? `Current: ${resource.file_name} (choose to replace)` : 'Choose a file (PDF, Word, Excel, PowerPoint, image; up to 25 MB)'}</span>
                <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.png,.jpg,.jpeg" data-testid="resource-file-input" />
              </label>
            </div>
          )}
          <div><Label>States</Label><StatePicker all={states} value={form.states} onChange={(v) => set('states', v)} /></div>
          <label className="flex items-center gap-3 text-sm">
            <Switch checked={form.featured} onCheckedChange={(v) => set('featured', v)} data-testid="resource-featured-switch" /> Featured resource
          </label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="resource-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{resource?.status === 'published' ? 'Save' : 'Save as Draft'}
          </Button>
          {resource?.status !== 'published' && (
            <Button onClick={() => save(true)} disabled={saving} data-testid="resource-save-publish-btn">Save & Publish</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
